import {
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    Modal,
    ModalDialog,
    Typography
} from "@mui/joy";
import {faBomb, faTriangleExclamation} from "@fortawesome/pro-regular-svg-icons";
import {ScheduledTransfer} from "../rpc/webrpc-client.gen.ts";
import {useTranslation} from "react-i18next";
import {useNavigate} from "@tanstack/react-router";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {ActionButton} from "../components/ActionButton.tsx";
import {useDisclaimScheduledTransferMutation} from "../tq/tqx.tsx";
import {z} from "zod/v4";

export const DisclaimScheduledTransferArgs = z.object({
    transferId: z.string(),
    removeTruck: z.optional(z.boolean()),
});
export type DisclaimScheduledTransferArgs = z.infer<typeof DisclaimScheduledTransferArgs>;

export const disclaimScheduledTransferVerb = "DisclaimScheduledTransfer";

export function disclaimScheduledTransferTqk(args: DisclaimScheduledTransferArgs) {
    return [disclaimScheduledTransferVerb, args] as const;
}

export interface DisclaimScheduledTransferDialogProps {
    transfer: ScheduledTransfer;
    removeTruck?: boolean;
    isOpen: boolean;
    close: () => void;
}

export function DisclaimScheduledTransferDialog(props: DisclaimScheduledTransferDialogProps) {
    const {t} = useTranslation();
    const transfer = props.transfer;
    const navigate = useNavigate();

    // TODO whether to disclaim truck as well
    // TODO disclaim all at once
    const {
        mutateAsync: performDisclaim, error: disclaimError,
        isPending: disclaimPending, reset: resetDisclaim
    } = useDisclaimScheduledTransferMutation();

    return <>
        <Modal open={props.isOpen}>
            <ModalDialog variant="outlined" role="alertdialog">
                <DialogTitle>
                    <FontAwesomeIcon icon={faTriangleExclamation}/>
                    <Typography level="title-lg">{t("confirm")}</Typography>
                </DialogTitle>
                <DialogContent>
                    {t("driver.reallyAbandonPrompt", {task: transfer.job.displayName})}
                </DialogContent>
                <DialogActions>
                    <ActionButton variant="solid" color="danger" perform={() =>
                        performDisclaim({transferId: transfer.id})
                            .then(() => {
                                props.close();
                            })
                            .then(() => navigate({to: "/driver/schedule"}))}>
                        {t("driver.yesAbandon")}
                    </ActionButton>
                    <Button variant="soft" color="neutral" onClick={props.close}>
                        {t("cancel")}
                    </Button>
                </DialogActions>
            </ModalDialog>
        </Modal>
        <Modal open={!!disclaimError}>
            <ModalDialog variant="outlined" role="alertdialog">
                <DialogTitle>
                    <FontAwesomeIcon icon={faBomb}/>
                    <Typography level="title-lg">{t("error")}</Typography>
                </DialogTitle>
                <DialogContent>
                    {disclaimError?.message}
                </DialogContent>
                <DialogActions>
                    <Button variant="soft" color="neutral" onClick={() => {
                        resetDisclaim();
                    }}>OK</Button>
                </DialogActions>
            </ModalDialog>
        </Modal>
        <Modal open={disclaimPending}>
            <ModalDialog variant="outlined">
                <DialogTitle>
                    <Typography level="title-lg">{t("driver.disclaiming")}</Typography>
                </DialogTitle>
                <DialogContent>
                    <LinearProgress/>
                </DialogContent>
            </ModalDialog>
        </Modal>
    </>
}

